import { supabase, requireSession } from "@/lib/supabase/client";
import type { WorkoutCompletion } from "@/types";
import { COMPLETION_SCHEMA_VERSION, migrateCompletion } from "@/lib/data/schemaVersions";
import { reliableWrite } from "@/lib/persistence/reliableWrite";

/**
 * Completion Service — reads and writes WorkoutCompletion records to Supabase.
 *
 * Individual columns are used here (not JSONB) because completions may be
 * aggregated or filtered server-side in the future (e.g. weekly TSS, charts).
 */
export const completionService = {
  /**
   * Load all completions for the current user.
   */
  async loadAll(userId: string): Promise<WorkoutCompletion[]> {
    const { data, error } = await supabase
      .from("workout_completions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(
        `[completionService.loadAll] failed (code: ${error.code}): ${error.message}`,
      );
      return [];
    }

    return (data ?? []).map((row) => {
      const extra = row.extra as {
        source?: string;
        actual_duration_min?: number;
        duration_total_sec?: number;
        power_avg?: number;
        power_np?: number;
        heart_rate_avg?: number;
        cadence_avg?: number;
        distance_m?: number;
        ascent_m?: number;
        calories?: number;
        speed_avg_ms?: number;
        speed_moving_avg_ms?: number;
        work_accum_joules?: number;
        fit_file_url?: string;
        wahoo_workout_id?: number;
        wahoo_workout_name?: string;
        workout_type_id?: number;
        workout_type?: string;
        best_efforts?: {
          max1s?: number;
          sprint5s?: number;
          min1?: number;
          min2?: number;
          min3?: number;
          vo2_5min?: number;
          min7?: number;
          min10?: number;
          threshold20min?: number;
          min30?: number;
          endurance60min?: number;
        };
        schema_version?: number;
        execution_analysis?: WorkoutCompletion["executionAnalysis"];
      } | null;
      const fromWebhook =
        extra?.source === "wahoo_webhook" || extra?.source === "wahoo_api";
      return migrateCompletion({
        workoutId: row.workout_id,
        status: row.status as WorkoutCompletion["status"],
        // Use the date embedded in workout_id as the primary source of truth
        // (e.g. "2026-04-06-workout-mon" → "2026-04-06"). This is set at plan
        // generation time and is immune to timezone corruption. Fall back to
        // row.date (actual ride date), then row.created_at as last resort.
        completedAt:
          (row.workout_id as string | null)?.match(
            /^(\d{4}-\d{2}-\d{2})/,
          )?.[1] ??
          row.date ??
          row.created_at,
        rpe: row.rpe ?? undefined,
        perceivedEffort: (row.rpe ??
          undefined) as WorkoutCompletion["perceivedEffort"],
        tssScore: row.tss_score ?? undefined,
        // Webhook note is internal — show blank so user can write their own
        notes:
          fromWebhook && row.notes === "Auto-synced from Wahoo ELEMNT"
            ? undefined
            : (row.notes ?? undefined),
        actualDurationMinutes: extra?.actual_duration_min ?? undefined,
        fromWebhook,
        powerAvgWatts: extra?.power_avg ?? undefined,
        powerNpWatts: extra?.power_np ?? undefined,
        heartRateAvg: extra?.heart_rate_avg ?? undefined,
        cadenceAvg: extra?.cadence_avg ?? undefined,
        distanceKm: extra?.distance_m
          ? Math.round(extra.distance_m / 100) / 10
          : undefined,
        ascentM: extra?.ascent_m ? Math.round(extra.ascent_m) : undefined,
        calories: extra?.calories ? Math.round(extra.calories) : undefined,
        speedAvgKph: extra?.speed_avg_ms
          ? Math.round(extra.speed_avg_ms * 3.6 * 10) / 10
          : undefined,
        speedMovingAvgKph: extra?.speed_moving_avg_ms
          ? Math.round(extra.speed_moving_avg_ms * 3.6 * 10) / 10
          : undefined,
        workKj: extra?.work_accum_joules
          ? Math.round(extra.work_accum_joules / 1000)
          : undefined,
        elapsedDurationMinutes: extra?.duration_total_sec
          ? Math.round(extra.duration_total_sec / 60)
          : undefined,
        fitFileUrl: extra?.fit_file_url ?? undefined,
        wahooWorkoutId: extra?.wahoo_workout_id ?? undefined,
        wahooWorkoutName: extra?.wahoo_workout_name ?? undefined,
        workoutTypeId:
          extra?.workout_type_id && extra.workout_type_id > 0
            ? extra.workout_type_id
            : undefined,
        workoutType: extra?.workout_type ?? undefined,
        bestEfforts: extra?.best_efforts ?? undefined,
        executionAnalysis: extra?.execution_analysis ?? undefined,
        schemaVersion: extra?.schema_version ?? undefined,
      });
    }).filter((value): value is WorkoutCompletion => value !== null);
  },

  /**
   * Upsert a single completion. Uses the unique(user_id, workout_id) constraint
   * so saving the same workout slot twice updates rather than inserts.
   */
  async save(userId: string, completion: WorkoutCompletion): Promise<void> {
    await requireSession(userId);
    // Only include tss_score when we actually have a value — omitting it on
    // conflict means Supabase won't touch the existing column, so a manual
    // RPE save won't overwrite a TSS that was written by the Wahoo webhook.
    // Only include extra when wahoo metrics are present (frontend activity import).
    // Omitting extra on a plain RPE/notes save preserves the webhook's richer data.
    // Only write extra when we have rich data (fit file URL present = came from
    // webhook or single-workout API fetch). The list-endpoint auto-import sets
    // wahooWorkoutId but has no fitFileUrl — never let it overwrite a richer
    // webhook extra that may already be in the DB.
    const hasWahooMetrics =
      completion.wahooWorkoutId !== undefined &&
      completion.fitFileUrl !== undefined;
    const hasRichAnalysis = completion.bestEfforts !== undefined || completion.executionAnalysis !== undefined;
    const fitExtra = hasRichAnalysis
      ? { source: "fit_import" as const, best_efforts: completion.bestEfforts, execution_analysis: completion.executionAnalysis, schema_version: COMPLETION_SCHEMA_VERSION }
      : undefined;
    const extra = hasWahooMetrics
      ? {
          source: "wahoo_api",
          wahoo_workout_id: completion.wahooWorkoutId,
          wahoo_workout_name: completion.wahooWorkoutName,
          actual_duration_min: completion.actualDurationMinutes,
          power_avg: completion.powerAvgWatts,
          power_np: completion.powerNpWatts,
          heart_rate_avg: completion.heartRateAvg,
          cadence_avg: completion.cadenceAvg,
          distance_m:
            completion.distanceKm != null
              ? Math.round(completion.distanceKm * 1000)
              : undefined,
          ascent_m: completion.ascentM,
          calories: completion.calories,
          speed_avg_ms:
            completion.speedAvgKph != null
              ? Math.round((completion.speedAvgKph / 3.6) * 100) / 100
              : undefined,
          speed_moving_avg_ms:
            completion.speedMovingAvgKph != null
              ? Math.round((completion.speedMovingAvgKph / 3.6) * 100) / 100
              : undefined,
          work_accum_joules:
            completion.workKj != null ? completion.workKj * 1000 : undefined,
          fit_file_url: completion.fitFileUrl,
          workout_type_id: completion.workoutTypeId,
          workout_type: completion.workoutType,
          best_efforts: completion.bestEfforts,
          execution_analysis: completion.executionAnalysis,
          schema_version: COMPLETION_SCHEMA_VERSION,
        }
      : fitExtra;

    return reliableWrite(`completion:${userId}:${completion.workoutId}`, async () => {
    const { error } = await supabase.from("workout_completions").upsert(
      {
        user_id: userId,
        workout_id: completion.workoutId,
        date: completion.completedAt.slice(0, 10),
        status: completion.status,
        rpe: completion.perceivedEffort ?? completion.rpe ?? null,
        notes: completion.notes ?? null,
        ...(completion.tssScore !== undefined && {
          tss_score: completion.tssScore,
        }),
        ...(extra !== undefined && { extra }),
      },
      { onConflict: "user_id,workout_id" },
    );

    if (error)
      throw new Error(
        `[completionService.save] ${error.message} (code: ${error.code})${
          error.hint ? ` — ${error.hint}` : ""
        }`,
      );
    });
  },

  /**
   * Bulk-insert completions (used by migration utility).
   * Silently skips duplicates via the unique constraint.
   */
  async bulkSave(
    userId: string,
    completions: WorkoutCompletion[],
  ): Promise<void> {
    if (completions.length === 0) return;

    const rows = completions.map((c) => ({
      user_id: userId,
      workout_id: c.workoutId,
      date: c.completedAt.slice(0, 10),
      status: c.status,
      rpe: c.perceivedEffort ?? c.rpe ?? null,
      tss_score: c.tssScore ?? null,
      notes: c.notes ?? null,
    }));

    const { error } = await supabase
      .from("workout_completions")
      .upsert(rows, { onConflict: "user_id,workout_id" });

    if (error) throw new Error(`[completionService.bulkSave] ${error.message}`);
  },
};
